import RemoveCircle from "@mui/icons-material/RemoveCircle";

import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { useCallback, useEffect, useState } from "react";
import { OrganAtRiskOut, OrganAtRiskIn } from "@providers/hop-ord-server/api";
import OarDialog from "./OarDialog";

export interface OarInfo extends OrganAtRiskOut {
  errors?: string[];
  touched?: string[];
}
interface OarTableProps {
  isReadOnly: boolean;
  updateOar: (oar: OrganAtRiskIn) => boolean;
  removeOar: (id: string | number) => void;
  addOar: () => void;
  oarData: OarInfo[];
  organOptions: string[];
  submitCount?: number;
  reportValidity: (isValid: boolean) => void;
  dataTestId: string;
  doseUnit: string;
  isImported?: boolean;
}

const OarTable = ({
  reportValidity,
  oarData,
  removeOar,
  addOar,
  updateOar,
  organOptions,
  dataTestId,
  isReadOnly,
  doseUnit,
}: OarTableProps) => {
  const isEditable = !isReadOnly;

  const [oarDialogOpen, setOarDialogOpen] = useState(false);
  const [oarTitle, setOarTitle] = useState("Add Organ At Risk Constraint");
  const [oarIndex, setOarIndex] = useState(-1);

  const addOarWrapper = () => {
    const anyEmpty = oarData.some((oar) => oar.organ === "");
    if (anyEmpty) {
      return;
    }
    addOar();
    setOarTitle("Add Organ At Risk Constraint");
    setOarIndex(oarData.length);
    setOarDialogOpen(true);
  };

  const validateOars = useCallback(
    (oarData: OarInfo[]) => {
      // check that all organ fields are filled
      const valid = oarData.every((oar) => oar["organ"] !== "");
      reportValidity(valid);
    },
    [reportValidity],
  );

  useEffect(() => {
    // TODO: Temporary site validation function until we can ingrain things into the datatable better
    validateOars(oarData);
  }, [validateOars, oarData]);

  const hasErrors = oarData.some((oar) => {
    if (oar.errors === undefined) {
      return false;
    }
    return oar.errors.some((err) => oar.touched?.includes(err));
  });

  return (
    <Card
      data-testid={dataTestId}
      style={{ marginLeft: "2px", marginRight: "2px" }}
    >
      <CardHeader
        title="Organs At Risk"
        slotProps={{
          title: {
            variant: "subtitle1",
          },
        }}
      />
      {hasErrors && <Alert severity="error">Missing required fields</Alert>}
      <CardContent
        style={{
          paddingTop: 0,
        }}
      >
        <Stack spacing={1}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="40%">Organ</TableCell>
                <TableCell width="50%">Constraints</TableCell>
                <TableCell align="center">Edit</TableCell>
                <TableCell align="center">Remove</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {oarData.map((oar, index) => (
                <TableRow key={index}>
                  <TableCell data-testid={`oar-organ-${index}`}>
                    {oar.organ}
                  </TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: "pre-wrap",
                    }}
                    data-testid={`oar-constraints-${index}`}
                  >
                    {oar.constraints}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      onClick={() => {
                        setOarIndex(index);
                        setOarTitle("Edit Organ At Risk Constraint");
                        setOarDialogOpen(true);
                      }}
                      data-testid={`oar-edit-${index}`}
                      disabled={!isEditable}
                    >
                      <EditIcon />
                    </Button>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      onClick={() => removeOar(oar.id)}
                      disabled={!isEditable}
                      data-testid={`oar-edit-${index}`}
                    >
                      <RemoveCircle />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <OarDialog
            open={oarDialogOpen}
            onClose={() => setOarDialogOpen(false)}
            onSubmit={(oar) => {
              setOarDialogOpen(false);
              updateOar(oar);
            }}
            organAtRisk={oarData[oarIndex]}
            organOptions={organOptions}
            doseUnit={doseUnit}
            oarTitle={oarTitle}
          />
          {isEditable && (
            <Stack
              direction="row"
              sx={{
                justifyContent: "start",
                paddingTop: 1,
              }}
            >
              <Button
                onClick={addOarWrapper}
                variant="outlined"
                startIcon={<AddIcon />}
                data-testid="add-oar"
              >
                Add OAR
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default OarTable;
